/**
 * Calculate all specific dates for a camp based on its date range and selected days
 * 
 * @param {Object} camp - The camp object
 * @param {string} camp.startDate - Camp start date (ISO string or Date)
 * @param {string} camp.endDate - Camp end date (ISO string or Date)
 * @param {Object} camp.campDays - Object with day selections (e.g., { monday: true, tuesday: false, ... })
 * @returns {string[]} Array of date strings in format 'YYYY-MM-DD'
 * 
 * Example:
 * If camp runs from Nov 11-20, 2025 and only Monday is selected,
 * returns ['2025-11-11', '2025-11-18'] (only the Mondays in that range)
 */
export const calculateCampDates = (camp) => {
  if (!camp || !camp.startDate || !camp.endDate || !camp.campDays) {
    return [];
  }

  const dates = [];
  const startDate = new Date(camp.startDate);
  const endDate = new Date(camp.endDate);

  // Map day names to JavaScript day numbers (0 = Sunday, 1 = Monday, etc.)
  const dayMapping = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  // Get selected day numbers
  const selectedDayNumbers = Object.entries(camp.campDays)
    .filter(([day, isSelected]) => isSelected)
    .map(([day]) => dayMapping[day.toLowerCase()]);

  // If no days selected, return empty array
  if (selectedDayNumbers.length === 0) {
    return [];
  }

  // Iterate through each date in the range
  const currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    const dayOfWeek = currentDate.getDay();
    
    // Check if this day of week is selected
    if (selectedDayNumbers.includes(dayOfWeek)) {
      // Format date as YYYY-MM-DD
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
};

/**
 * Format a date object to YYYY-MM-DD string
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateToKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Check if a date falls within a camp's range and matches selected days
 * @param {Date} date - The date to check
 * @param {Object} camp - The camp object
 * @returns {boolean} True if date is a camp date
 */
export const isDateInCamp = (date, camp) => {
  if (!camp || !camp.startDate || !camp.endDate || !camp.campDays) {
    return false;
  }

  const checkDate = new Date(date);
  const startDate = new Date(camp.startDate);
  const endDate = new Date(camp.endDate);

  // Check if date is within range
  if (checkDate < startDate || checkDate > endDate) {
    return false;
  }

  // Check if day of week matches
  const dayMapping = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6,
  };

  const dayOfWeek = checkDate.getDay();
  const dayName = Object.keys(dayMapping).find(key => dayMapping[key] === dayOfWeek);
  
  return camp.campDays[dayName] === true;
};

/**
 * Get a human-readable description of camp days
 * @param {Object} campDays - Object with day selections
 * @returns {string} Description like "Mon, Wed, Fri" or "Weekdays" or "Every day"
 */
export const getCampDaysDescription = (campDays) => {
  if (!campDays) return 'No days selected';

  const selectedDays = Object.entries(campDays)
    .filter(([day, isSelected]) => isSelected)
    .map(([day]) => day);

  if (selectedDays.length === 0) return 'No days selected';
  if (selectedDays.length === 7) return 'Every day';

  const weekdayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const dayAbbreviations = {
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
  };

  // Check for weekdays pattern
  const weekdays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
  const isWeekdays = weekdays.every(day => selectedDays.includes(day)) && 
                     !selectedDays.includes('saturday') && 
                     !selectedDays.includes('sunday');
  if (isWeekdays) return 'Weekdays';

  // Check for weekend pattern
  const weekend = ['saturday', 'sunday'];
  const isWeekend = weekend.every(day => selectedDays.includes(day)) && 
                    weekdays.every(day => !selectedDays.includes(day));
  if (isWeekend) return 'Weekends';

  // Return abbreviated day names
  return selectedDays
    .sort((a, b) => weekdayOrder.indexOf(a) - weekdayOrder.indexOf(b))
    .map(day => dayAbbreviations[day])
    .join(', ');
};
